import { Component, Injector, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * BudgetedCapex Widget
 */
@Component({
  selector: 'budgeted-capex-widget',
  templateUrl: './budgeted-capex-widget.component.html',
  styleUrls: ['../../common/css/general.scss', '../../common/css/text-and-list.scss', './budgeted-capex-widget.component.scss']
})
export class BudgetedCapexWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Delete current row subject
   */
  private deleteCurrentRowSubject = new Subject<void>();

  /**
   * Delete current row observable
   */
  public deleteCurrentRow$ = this.deleteCurrentRowSubject.asObservable();

  /**
   * Add row subject
   */
  private addRowSubject = new Subject<any>();

  /**
   * Add row observable
   */
  public addRow$ = this.addRowSubject.asObservable();

  /**
   * Move current row up subject
   */
  private moveCurrentRowUpSubject = new Subject<void>();

  /**
   * Move current row up observable
   */
  public moveCurrentRowUp$ = this.moveCurrentRowUpSubject.asObservable();

  /**
   * Move current row down subject
   */
  private moveCurrentRowDownSubject = new Subject<void>();

  /**
   * Move current row down observable
   */
  public moveCurrentRowDown$ = this.moveCurrentRowDownSubject.asObservable();

  /**
   * Text & List section rows
   */
  rows: any = [];

  /**
   * Text & List section name
   */
  readonly textAndListSectionName: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'budgetedCapex', false);
    this.textAndListSectionName = `${this.name}`;//should be the same as defined in repository
  }

  /**
   * Widget's text area options (if used)
   */
  textAreaOptions: any = null;

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("budgetedCapex", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.rows = this.getTextAndListSettingsOrSetDefault(this.textAndListSectionName, [
      {
        topicId: 1,
        fieldName: 'maintenanceCapexContingency',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 2,
        fieldName: 'refurbishmentOfCoolingTower',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 3,
        fieldName: 'groundMainSwitchboardAtsRepair',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 4,
        fieldName: 'mainSwitchAcbRepairsAsbestosArcChuteRemovalAndDisposal',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 5,
        fieldName: 'newCablingFromIsolatorToNewVsds',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 6,
        fieldName: 'firePenetrationRectification',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 7,
        fieldName: 'liftSystemOverlayOfEndOfLifeGroupContollers',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 8,
        fieldName: 'p2000ControllerHardwareUpgrade',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 9,
        fieldName: 'officeSignage',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }, {
        topicId: 10,
        fieldName: 'criticalEolReplacementPlanning',
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true
      }
    ]);

    this.loadReflist('ref_l3_budgeted_capex_topics');

    this.mockupData$ = this.widgetService.loadMockup('budgetedCapex').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('budgetedCapex', this.getReport()).pipe();
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Delete current row event handler
   */
  public deleteCurrentRow(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.deleteCurrentRowSubject.next();
  }

  /**
   * Add row event handler
   */
  public addRow(): void {
    const unselectedFields = this.rows.filter(item => !item.selected);
    if (unselectedFields.length > 0) {
      this.setTextAndListSettings(this.textAndListSectionName, this.rows);
      this.addRowSubject.next({
        topicId: this.getTopicId(),
        fieldName: null,
        decimals: 0,
        thousandsSeparators: true,
        isNumericField: true,
        isCurrency: true,
        isArea: false,
        selected: true,
        useInTotal: true,
        lastTopicId: this.getTopicId()
      });
    }
  }

  /**
   * Move current row up event handler
   */
  public moveCurrentRowUp(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowUpSubject.next();
  }

  /**
   * Move current row down event handler
   */
  public moveCurrentRowDown(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.moveCurrentRowDownSubject.next();
  }

  /**
   * Calculate total value
   */
  public getTotal(): number {
    let total = 0;

    for (const row of this.rows) {
      if (row.useInTotal && row.selected) {
        total += this.widgetData[row.fieldName];
      }
    }

    return total;
  }

  /**
   * Data changed event handler
   */
  public onDataChanged(): void {
    this.setTextAndListSettings(this.textAndListSectionName, this.rows);
    this.setDirtyFlag(true);
  }

  /**
   * Get topic Id
   */
  private getTopicId() {
    const unselectedFields = this.rows.filter(item => !item.selected);
    return Math.min(...unselectedFields.map(item => item.topicId));
  }
}
